import React, { useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import qs from 'qs';
import domainmodel from 'domain-model';
import Router from 'next/router';
import axios from 'axios';
import IconSearch from '../icons/IconSearch';
import getAxiosInstance from '../../config/api';
import useHamburgerMenu from './hamburgerMenuHook';

let articleCancelTokenSource = null;
let supplierCancelTokenSource = null;
let brandCancelTokenSource = null;

const MINUMUM_NUMBER_OF_CHARS_FOR_SEARCH = 3;
const { createSearchStringQuery } = domainmodel.pricedArticle;

/*
  TODO: This component should be broken out to a reduxbacked component where we can test the behaviour.
  The component can just render the storestate from redux and we can test debounce behaviour to minimize number of calls.
  Debounce does not play well with loading options callback becuase of react-renders.
*/

const Group = props => (
  /* eslint-disable react/jsx-props-no-spreading */
  <div>
    <components.Group {...{ ...props }} />
  </div>
);
/* eslint-enable */
const Input = props => (
  /* eslint-disable react/jsx-props-no-spreading */
  <div>
    <components.Input {...{ ...props, isHidden: false }} />
  </div>
);
/* eslint-enable */
const LoadingIndicator = props => (
  /* eslint-disable react/jsx-props-no-spreading */
  <div style={{ position: 'absolute', right: '20px', top: '20%' }}>
    <components.LoadingIndicator {...{ ...props }} />
  </div>
);
/* eslint-enable */
const customStyles = {
  container: provided => ({
    ...provided,
    width: '100%',
  }),
  input: provided => ({
    ...provided,
    fontSize: '1.2rem',
  }),
  menu: provided => ({
    ...provided,
    borderRadius: 0,
    marginTop: 0,
    zIndex: 3000,
  }),
  menuList: provided => ({
    ...provided,
    textAlign: 'left',
  }),
  option: (provided, { isFocused }) => ({
    ...provided,
    color: 'black',
    backgroundColor: isFocused ? '#bcac76' : 'white',
  }),
  singleValue: provided => ({ ...provided, position: 'absolute' }),
  control: (provided, state) => ({
    ...provided,
    border: 'none',
    borderRadius: 0,
    width: '100%',
    borderBottom: state.isFocused ? '2px solid #1f1f1f' : '3px solid #eee ',
    transition: 'all .5s;',
  }),
  placeholder: provided => ({
    ...provided,
    position: 'absolute', // this is due to newer version of reactstrap using grid. could maybe be solved with some sort of flexbox instead
  }),
};

const createDropdownItems = (responseData, entityType) =>
  responseData.rows.map(x => ({
    routerParams: {
      pathname: `/${entityType}s/${entityType === 'supplier' ? x.name : x.id}`,
    },
    label: x.name,
    value: x.name,
  }));

// eslint-disable-next-line consistent-return
const loadOptions = async (searchString, callBack) => {
  if (searchString.length < MINUMUM_NUMBER_OF_CHARS_FOR_SEARCH) return [];
  const axiosInstance = getAxiosInstance();
  const articleQuery = qs.stringify({
    query: createSearchStringQuery(searchString),
    pageSize: 3,
    sort: 'similarity.-name',
    searchString,
  });
  const query = qs.stringify({
    query: { name: { iLike: `${searchString}%` } },
    pageSize: 3,
    sort: 'similarity.-name',
    searchString,
  });

  if (
    articleCancelTokenSource ||
    supplierCancelTokenSource ||
    brandCancelTokenSource
  ) {
    articleCancelTokenSource.cancel();
    brandCancelTokenSource.cancel();
    supplierCancelTokenSource.cancel();
  }
  articleCancelTokenSource = axios.CancelToken.source();
  supplierCancelTokenSource = axios.CancelToken.source();
  brandCancelTokenSource = axios.CancelToken.source();
  const [articles, brands, suppliers] = await Promise.all([
    axiosInstance.get(`/public/articles?${articleQuery}`, {
      cancelToken: articleCancelTokenSource.token,
    }),
    axiosInstance.get(`/public/brands?${query}`, {
      cancelToken: brandCancelTokenSource.token,
    }),
    axiosInstance.get(`/public/suppliers?${query}`, {
      cancelToken: supplierCancelTokenSource.token,
    }),
  ]);
  articleCancelTokenSource = null;
  brandCancelTokenSource = null;
  supplierCancelTokenSource = null;

  if (!articles.data.total && !suppliers.data.total && !brands.data.total) {
    return [{ label: 'Inga träffar' }];
  }
  const articleOptions = createDropdownItems(articles.data, 'article');
  if (articles.data.total > articles.data.rows.length) {
    articleOptions.push({
      label: `Se alla ${articles.data.total} matchande artiklar`,
      routerParams: {
        pathname: '/articles',
        query: {
          filter: qs.stringify(createSearchStringQuery(searchString)),
          searchName: searchString,
          sort: 'similarity.-name',
          searchString,
        },
      },
    });
  }

  const brandOptions = createDropdownItems(brands.data, 'brand');
  const supplierOptions = createDropdownItems(suppliers.data, 'supplier');
  callBack([
    {
      label: 'Artiklar',
      options: articleOptions,
    },
    {
      label: 'Varumärken',
      options: brandOptions,
    },
    {
      label: 'Leverantörer',
      options: supplierOptions,
    },
  ]);
};

function SearchInput({ closeFocus, hideSearchIcon, shopConfig }) {
  const [selectedValue, setSelectedValue] = useState(undefined);
  const [inputValue, setInputValue] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const { closeHamburgerMenu } = useHamburgerMenu();

  const handleSubmit = dropdownOption => {
    Router.push(dropdownOption.routerParams);
  };

  const handleFreeTextSubmit = searchString => {
    const searchStringQuery = createSearchStringQuery(searchString, shopConfig);
    setSelectedValue({ label: '', value: null });
    handleSubmit({
      routerParams: {
        pathname: '/articles',
        query: {
          filter: qs.stringify(searchStringQuery),
          searchName: searchString,
          sort: 'similarity.-name',
        },
      },
    });
  };

  const handleSelectChange = searchOption => {
    if (_.has(searchOption, '__isNew__')) {
      handleFreeTextSubmit(searchOption.value);
      return;
    }
    handleSubmit(searchOption);
    setSelectedValue(searchOption);
    setMenuIsOpen(false);
  };

  // preventing default of clearing input on blur
  const handleInputChange = (value, { action }) => {
    if (action === 'menu-close' || !value) {
      setMenuIsOpen(false);
      closeFocus();
      closeHamburgerMenu();
    }
    if (action === 'input-blur' && !value) {
      setInputValue(null);
      setSelectedValue(null);
      closeFocus();
    }
    if (action === 'input-change') {
      setInputValue(value);
      if (!value) {
        setSelectedValue(null);
      }
      return value;
    }
    return inputValue;
  };

  const keyDown = key => {
    const enterKey = key.keyCode === 13;
    const escKey = key.keyCode === 27;
    const backSpace = key.keyCode === 8;

    if (!menuIsOpen && enterKey) {
      handleFreeTextSubmit(inputValue);
    } else if (escKey || (!inputValue && backSpace)) {
      setMenuIsOpen(false);
    } else {
      setMenuIsOpen(true);
    }
    if (enterKey) {
      closeFocus();
      closeHamburgerMenu();
    }
  };

  if (selectedValue) {
    closeFocus();
  }

  return (
    <div className="d-flex">
      {!hideSearchIcon && (
        <div className="pt-2 me-1" color="none" style={{ width: '25px' }}>
          <IconSearch strokeWidth="5" stroke="white" />
        </div>
      )}

      <AsyncSelect
        id="shop-search-input"
        instanceId="shop-search-input"
        placeholder="Sök"
        cacheOptions
        allowCreateWhileLoading
        noOptionsMessage={() => 'Inga träffar'} // This never happens at the moment, because of createOption
        openMenuOnClick={false}
        menuShouldScrollIntoView={false}
        menuIsOpen={menuIsOpen}
        value={selectedValue}
        loadOptions={loadOptions}
        onChange={handleSelectChange}
        onInputChange={handleInputChange}
        components={{
          DropdownIndicator: null,
          Group,
          Input,
          LoadingIndicator,
        }}
        styles={customStyles}
        formatCreateLabel={searchString =>
          searchString.length < MINUMUM_NUMBER_OF_CHARS_FOR_SEARCH
            ? `Skriv minst tre bokstäver för att söka`
            : `Sökresultat för "${searchString}"`
        }
        createOptionPosition="first"
        onCreateOption={handleFreeTextSubmit}
        onKeyDown={keyDown}
        theme={theme => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: 'black',
          },
        })}
        classNames={{
          container: () => 'search-input-container',
          control: () => 'search-input-control',
          menuList: () => 'search-input-menu-list',
          menu: () => 'search-input-menu',
          menuPortal: () => 'search-input-menu-portal',
          option: () => 'search-input-option',
          groupHeading: () => 'search-input-group-heading',
          group: () => 'search-input-group',
          placeholder: () => 'search-input-placeholder',
          input: () => 'search-input-input',
        }}
      />
    </div>
  );
}

SearchInput.propTypes = {
  closeFocus: PropTypes.func,
  hideSearchIcon: PropTypes.bool,
};

SearchInput.defaultProps = {
  closeFocus: () => {},
  hideSearchIcon: false,
};

export default SearchInput;
